import { CheckCircle2, Wand2 } from 'lucide-react'
import type { ValidationError } from './validator/types'
import { canAutoResolve } from './validator/resolutions'

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  errors: ValidationError[]
  onAutoResolve: (error: ValidationError) => void
  onAcknowledge: (error: ValidationError) => void
}

// ─── Severity config ──────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  error:   { dot: 'bg-error-fg',   text: 'text-error-fg',   label: 'Error'   },
  warning: { dot: 'bg-warning-fg', text: 'text-warning-fg', label: 'Warning' },
  info:    { dot: 'bg-info-fg',    text: 'text-info-fg',    label: 'Info'    },
}

// ─── Banner row ───────────────────────────────────────────────────────────────

function BannerRow({
  error,
  onAutoResolve,
  onAcknowledge: _onAcknowledge,
}: {
  error: ValidationError
  onAutoResolve: (e: ValidationError) => void
  onAcknowledge: (e: ValidationError) => void
}) {
  const style = SEVERITY_STYLES[error.severity]
  const resolvable = canAutoResolve(error.code)

  return (
    <div className={`flex items-center gap-2.5 px-5 py-1.5 ${error.acknowledged ? 'opacity-40' : ''}`}>
      {/* Severity dot */}
      <span className={`shrink-0 h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden="true" />

      {/* Severity label */}
      <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider ${style.text}`}>
        {style.label}
      </span>

      {/* Message */}
      <span className="flex-1 text-xs text-fg">
        {error.message}
      </span>

      {/* Actions */}
      {!error.acknowledged && (
        <div className="flex shrink-0 items-center gap-1.5">
          {resolvable && (
            <button
              type="button"
              onClick={() => onAutoResolve(error)}
              className="flex items-center gap-1 rounded bg-accent-primary-solid px-1.5 py-0.5 text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Wand2 size={9} />
              Auto-fix
            </button>
          )}

        </div>
      )}
    </div>
  )
}

// ─── ArticleValidationBanner ──────────────────────────────────────────────────

/**
 * Always-visible sticky banner below the toolbar.
 * Header row summarises both article-level and block-level counts at a glance.
 * Followed by one row per article-scoped issue (block issues live in the gutter).
 */
export function ArticleValidationBanner({ errors, onAutoResolve, onAcknowledge }: Props) {
  const articleErrors = errors.filter(e => e.scope === 'article')
  const blockErrors   = errors.filter(e => e.scope !== 'article')

  const articleErrorCount   = articleErrors.filter(e => e.severity === 'error').length
  const articleWarningCount = articleErrors.filter(e => e.severity === 'warning').length
  const blockErrorCount     = blockErrors.filter(e => e.severity === 'error').length
  const blockWarningCount   = blockErrors.filter(e => e.severity === 'warning').length

  const hasAnyError   = articleErrorCount   > 0 || blockErrorCount   > 0
  const hasAnyWarning = articleWarningCount > 0 || blockWarningCount > 0

  const accentBorder = hasAnyError
    ? 'border-l-error-line'
    : hasAnyWarning
      ? 'border-l-warning-line'
      : 'border-l-success-line'

  const allClean = !hasAnyError && !hasAnyWarning

  return (
    <div
      className={`shrink-0 border-b border-line border-l-4 bg-surface ${accentBorder} divide-y divide-line`}
      role={hasAnyError || hasAnyWarning ? 'alert' : 'status'}
      aria-label="Article-level validation status"
    >
      {/* ── Summary header row ── */}
      <div className="flex items-center gap-4 px-5 py-1.5">
        {allClean ? (
          <>
            <CheckCircle2 size={12} className="shrink-0 text-success-fg" aria-hidden="true" />
            <span className="text-xs text-fg-secondary">No validation issues</span>
          </>
        ) : (
          <>
            {/* Article counts */}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-secondary">Article</span>
            {articleErrorCount > 0 && (
              <span className="text-[11px] text-error-fg">
                {articleErrorCount} {articleErrorCount === 1 ? 'error' : 'errors'}
              </span>
            )}
            {articleWarningCount > 0 && (
              <span className="text-[11px] text-warning-fg">
                {articleWarningCount} {articleWarningCount === 1 ? 'warning' : 'warnings'}
              </span>
            )}
            {articleErrorCount === 0 && articleWarningCount === 0 && (
              <span className="text-[11px] text-success-fg">OK</span>
            )}

            <span className="text-fg-secondary" aria-hidden="true">·</span>

            {/* Block counts */}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-secondary">Blocks</span>
            {blockErrorCount > 0 && (
              <span className="text-[11px] text-error-fg">
                {blockErrorCount} {blockErrorCount === 1 ? 'error' : 'errors'}
              </span>
            )}
            {blockWarningCount > 0 && (
              <span className="text-[11px] text-warning-fg">
                {blockWarningCount} {blockWarningCount === 1 ? 'warning' : 'warnings'}
              </span>
            )}
            {blockErrorCount === 0 && blockWarningCount === 0 && (
              <span className="text-[11px] text-success-fg">OK</span>
            )}
          </>
        )}
      </div>

      {/* ── Per-issue rows (article-scoped only) ── */}
      {articleErrors.map((err, i) => (
        <BannerRow
          key={`${err.code}-${i}`}
          error={err}
          onAutoResolve={onAutoResolve}
          onAcknowledge={onAcknowledge}
        />
      ))}
    </div>
  )
}
